import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from '../data/blogData';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="blog-post-page">
        <Helmet>
          <title>Post Not Found | Jajoga</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="blog-post-container">
          <div className="blog-not-found">
            <h1>Post Not Found</h1>
            <p>The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="back-to-blog">Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://jajoga.com/blog/${post.slug}`;
  const imageUrl = 'https://jajoga.com/images/hero-dashboard.png';

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{post.title} | Jajoga</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="Jajoga" />
        <meta property="article:published_time" content={`2026-01-15`} />
        <meta property="article:section" content={post.category} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={imageUrl} />

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "url": canonicalUrl,
            "image": imageUrl,
            "datePublished": "2026-01-15",
            "dateModified": "2026-01-15",
            "author": {
              "@type": "Organization",
              "name": "Jajoga"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Jajoga",
              "logo": {
                "@type": "ImageObject",
                "url": "https://jajoga.com/images/logo.svg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            }
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://jajoga.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://jajoga.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title
              }
            ]
          })}
        </script>
      </Helmet>
      <article className="blog-post">
        <header className="blog-post-header">
          <div className="container">
            <Link to="/blog" className="back-link">Back to Blog</Link>
            <span className="blog-category">{post.category}</span>
            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-post-meta">
              <span className="blog-date">{post.date}</span>
              <span className="blog-read-time">{post.readTime}</span>
            </div>
          </div>
        </header>

        <div className="blog-post-container">
          <div className="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="blog-post-cta">
            <h3>Ready to see what's happening in your salon?</h3>
            <p>Get the visibility you need to make confident decisions.</p>
            <button className="btn btn-primary" onClick={scrollToContact}>
              Show Me My Data
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
